import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Bundle, CodeableConcept, Procedure, ServiceRequest } from 'fhir/r4';
import { firstValueFrom } from 'rxjs';
import { backendEndPointToken } from '../app.config';
import { AuthService } from '../shared/auth/auth.service';
import { StateService } from '../shared/state.service';
import { FinalizeReportConfirmDialogComponent } from '../shared/finalize-report-confirm-dialog/finalize-report-confirm-dialog.component';
import { ProcedureFinalizationInventoryService } from '../shared/services/procedure-finalization-inventory.service';
import { UnifiedNotificationService } from '../shared/unified-notification.service';
import { NotificationService } from '../shared/notification/notification.service';

export type ProcedureReportDialogData = {
  serviceRequest: ServiceRequest;
  patientId: string;
  existingProcedure?: Procedure | null;
  readOnly?: boolean;
};

@Component({
  selector: 'app-procedure-report-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSnackBarModule,
  ],
  templateUrl: './procedure-report-dialog.component.html',
  styleUrls: ['./procedure-report-dialog.component.scss'],
})
export class ProcedureReportDialogComponent {
  private readonly data = inject<ProcedureReportDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<ProcedureReportDialogComponent, Procedure | null>);
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpClient);
  private readonly backend = inject(backendEndPointToken);
  private readonly auth = inject(AuthService);
  private readonly state = inject(StateService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly finalizationInventory = inject(ProcedureFinalizationInventoryService);
  private readonly unifiedNotificationService = inject(UnifiedNotificationService);
  private readonly notifications = inject(NotificationService);

  readonly serviceRequest = this.data.serviceRequest;
  readonly existingProcedure = this.data.existingProcedure ?? null;
  readonly readOnly = !!this.data.readOnly;
  saving = false;

  readonly form = this.fb.group({
    performedDateTime: [this.existingProcedure?.performedDateTime ?? new Date().toISOString().slice(0, 16)],
    status: [this.existingProcedure?.status ?? 'in-progress'],
    outcome: [this.codeableText(this.existingProcedure?.outcome)],
    complications: [this.codeableTexts(this.existingProcedure?.complication).join('\n')],
    followUp: [this.codeableTexts(this.existingProcedure?.followUp).join('\n')],
    note: [this.existingProcedure?.note?.map((n) => n.text).filter(Boolean).join('\n') ?? ''],
  });

  constructor() {
    if (this.readOnly) {
      this.form.disable();
    }
  }

  get title(): string {
    const code = this.serviceRequest?.code?.text
      || this.serviceRequest?.code?.coding?.[0]?.display
      || this.serviceRequest?.code?.coding?.[0]?.code
      || 'Procedure';
    return `${this.existingProcedure?.id ? 'Edit' : 'Add'} ${code}`;
  }

  close(): void {
    this.dialogRef.close(null);
  }

  async saveDraft(): Promise<void> {
    await this.save('in-progress');
  }

  async saveFinal(): Promise<void> {
    const currentStatus = (this.existingProcedure?.status ?? '').toString().toLowerCase();
    const srStatus = (this.serviceRequest?.status ?? '').toString().toLowerCase();
    if (currentStatus !== 'completed' && srStatus === 'active') {
      const confirmed = await firstValueFrom(this.dialog.open(FinalizeReportConfirmDialogComponent, {
        maxWidth: '520px',
        autoFocus: false,
        data: {
          expectedLastName: this.currentUserLastName(),
          title: 'Finalize Procedure',
          message: 'To save this Procedure as final and complete the linked ServiceRequest, type your last name to confirm.'
        }
      }).afterClosed());

      if (!confirmed) return;
    }

    await this.save('completed');
  }

  private async save(status: Procedure['status']): Promise<void> {
    if (this.readOnly || this.saving) return;
    if (!this.serviceRequest?.id || !this.data.patientId) {
      this.snackBar.open('Procedure context is missing.', 'Dismiss', { duration: 3500 });
      return;
    }

    this.saving = true;
    try {
      const payload = this.buildProcedure(status);
      
      const entries: any[] = [];
      entries.push({
        resource: payload,
        request: { method: payload.id ? 'PUT' : 'POST', url: payload.id ? `Procedure/${payload.id}` : 'Procedure' }
      });
      
      let updatedServiceRequest: ServiceRequest | undefined;
      const lowStockNotifications: Array<{ bundleIndex: number; notification: any }> = [];
      let inventoryExceptions: any[] = [];
      const srStatus = (this.serviceRequest?.status ?? '').toString().toLowerCase();
      if (status === 'completed' && srStatus !== 'completed') {
        // Resolve the service configuration and consume its configured supplies
        // in this same transaction as Procedure finalization.
        const finalization = await this.finalizationInventory.prepare(this.serviceRequest.id);
        inventoryExceptions = finalization.inventoryReconciliations.filter((item) => item.shortageQuantity > 0);
        updatedServiceRequest = finalization.serviceRequest;
        if (updatedServiceRequest?.id) {
          entries.push({
            resource: updatedServiceRequest,
            request: { method: 'PUT', url: `ServiceRequest/${updatedServiceRequest.id}` }
          });
        }

        for (const delivery of finalization.supplyDeliveries) {
          if (!delivery?.id) continue;
          entries.push({
            resource: delivery,
            request: { method: 'PUT', url: `SupplyDelivery/${delivery.id}` }
          });
        }

        for (const invoice of finalization.invoices) {
          if (!invoice?.id) continue;
          entries.push({
            resource: invoice,
            request: { method: 'PUT', url: `Invoice/${invoice.id}` }
          });
        }

        for (const alert of finalization.lowStockAlerts) {
          lowStockNotifications.push({ bundleIndex: entries.length, notification: alert.notification });
          entries.push({ resource: alert.resource, request: { method: 'POST', url: 'CommunicationRequest' } });
        }
      }

      const bundle: Bundle = {
        resourceType: 'Bundle',
        type: 'transaction',
        entry: entries
      };

      const savedBundle = await firstValueFrom(this.http.post<Bundle>(`${this.backend}`, bundle, { headers: this.fhirHeaders() as any }));
      
      let savedProcedure = savedBundle.entry?.find(e => e.resource?.resourceType === 'Procedure')?.resource as Procedure;
      if (!savedProcedure) {
        savedProcedure = payload;
        const loc = savedBundle.entry?.[0]?.response?.location;
        if (!savedProcedure.id && loc) {
            savedProcedure.id = loc.split('/')[1] || loc;
        }
      }

      this.state.persistLocalResource(savedProcedure as any, 'saved');
      this.state.processOrgWideBundleTransaction(savedBundle as any);

      for (const alert of lowStockNotifications) {
        const location = savedBundle.entry?.[alert.bundleIndex]?.response?.location || '';
        const communicationId = location.split('/')[1];
        this.unifiedNotificationService.notifyLowStockAlert({ ...alert.notification, ...(communicationId ? { communicationId } : {}) });
      }

      if (inventoryExceptions.length) {
        const details = inventoryExceptions.map((item) => {
          const unit = item.unit ? ` ${item.unit}` : '';
          if (!item.sourceBatchId) {
            return `${item.itemName}: ${item.requiredQuantity}${unit} required; no matching SupplyDelivery was found, so ${item.shortageQuantity}${unit} was not costed.`;
          }
          return `${item.itemName}: ${item.requiredQuantity}${unit} required; ${item.recordedQuantityUsed}${unit} deducted and ${item.shortageQuantity}${unit} estimated at the latest batch cost.`;
        }).join(' ');
        this.notifications.warning(`Procedure finalized with inventory exceptions. ${details} Verify Supply Stock and reconcile the delivery record.`, 12000);
      }

      if (updatedServiceRequest) {
        this.state.persistLocalResource(updatedServiceRequest as any, 'saved');
      }

      this.dialogRef.close(savedProcedure);
    } catch (error) {
      console.error(error);
      this.snackBar.open('Failed to save Procedure.', 'Dismiss', { duration: 4500 });
    } finally {
      this.saving = false;
    }
  }

  private buildProcedure(status: Procedure['status']): Procedure {
    const raw = this.form.getRawValue();
    const existing = this.stripRichData({ ...(this.existingProcedure ?? {} as Procedure) });
    const performedDateTime = raw.performedDateTime
      ? new Date(raw.performedDateTime).toISOString()
      : (existing.performedDateTime ?? new Date().toISOString());

    const procedure: Procedure = {
      ...existing,
      resourceType: 'Procedure',
      status,
      subject: existing.subject ?? { reference: `Patient/${this.data.patientId}` },
      basedOn: this.ensureReference(existing.basedOn as any[], `ServiceRequest/${this.serviceRequest.id}`) as any,
      code: existing.code ?? this.serviceRequest.code,
      performedDateTime,
      outcome: this.toCodeableConcept(raw.outcome ?? '') as any,
      complication: this.toCodeableConceptList(raw.complications ?? '') as any,
      followUp: this.toCodeableConceptList(raw.followUp ?? '') as any,
      note: this.toNotes(raw.note ?? '') as any,
    };

    if (!procedure.outcome) delete (procedure as any).outcome;
    if (!procedure.complication?.length) delete (procedure as any).complication;
    if (!procedure.followUp?.length) delete (procedure as any).followUp;
    if (!procedure.note?.length) delete (procedure as any).note;
    return procedure;
  }

  private stripRichData(obj: any): any {
    if (!obj) return obj;
    // ServiceRequest rows are enriched for the UI before this dialog is opened.
    // These fields are not part of the FHIR ServiceRequest model and must never
    // be included in the transaction bundle sent to the FHIR server.
    const uiKeys = [
      'available',
      'rawResource',
      'actualResource',
      'relatedChargeItems',
      'relatedInvoices',
      'hasBalancedInvoice',
      'Resource_Identifier',
      'systemReference',
    ];

    return Object.fromEntries(
      Object.entries(obj).filter(([key]) => !uiKeys.includes(key))
    );
  }

  private toCodeableConcept(value: string): CodeableConcept | undefined {
    const text = (value ?? '').toString().trim();
    return text ? { text } : undefined;
  }

  private toCodeableConceptList(value: string): CodeableConcept[] {
    return (value ?? '')
      .toString()
      .split(/\r?\n/)
      .map((text) => text.trim())
      .filter(Boolean)
      .map((text) => ({ text }));
  }

  private toNotes(value: string) {
    return (value ?? '')
      .toString()
      .split(/\r?\n/)
      .map((text) => text.trim())
      .filter(Boolean)
      .map((text) => ({ text, time: new Date().toISOString() }));
  }

  private ensureReference(refs: any[] | undefined, ref: string): any[] {
    const existing = Array.isArray(refs) ? refs : [];
    if (existing.some((r) => (r?.reference ?? '').toString() === ref || (r?.reference ?? '').toString().endsWith(`/${ref}`))) {
      return existing;
    }
    return [...existing, { reference: ref }];
  }

  private codeableText(value?: CodeableConcept): string {
    return (value?.text || value?.coding?.[0]?.display || value?.coding?.[0]?.code || '').toString();
  }

  private codeableTexts(values?: CodeableConcept[]): string[] {
    return (values ?? []).map((value) => this.codeableText(value)).filter(Boolean);
  }

  private currentUserLastName(): string {
    const user: any = this.auth.user.getValue();
    const fhirFamily = Array.isArray(user?.name) ? user.name?.[0]?.family : '';
    if (fhirFamily) return fhirFamily.toString();
    const name = (user?.display || user?.name || user?.email || '').toString().trim();
    return name.split(/\s+/).filter(Boolean).pop() || '';
  }

  private fhirHeaders() {
    return {
      'Content-Type': 'application/fhir+json',
      Accept: 'application/fhir+json',
      Prefer: 'return=representation',
    } as Record<string, string>;
  }
}
